import { useMemo, useState } from 'react'
import { Alert, Button, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { ClosedHiddenTag } from '@/shared/ui/table/ClosedHidden'
import { useClosedHidden } from '@/shared/ui/table/useClosedHidden'
import { STATE_ALL, STATE_OPEN_ONLY } from '@/shared/ui/table/closedFilter'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { EmploymentAssignModal } from '@/features/hr/EmploymentAssignModal'
import { OrgNodeTreeSelect } from '@/features/hr/OrgNodeTreeSelect'
import { PersonFormModal } from '@/features/hr/PersonFormModal'
import { useEmployeeNodes } from '@/features/hr/useEmployeeNodes'
import {
  PERSON_STATES,
  countClosedPersons,
  fetchJobTitles,
  fetchPersonPage,
  type Person,
  type PersonState,
  type PersonStateFilter,
} from '@/features/hr/hrApi'

/**
 * Holat rangi holatning MA'NOSINI ko'rsatadi (`StatusTag.tsx` qoidasi):
 *   · `ACTIVE`     — yashil, ishlayapti
 *   · `SUSPENDED`  — sariq, VAQTINCHA to'xtatilgan, qaytariladi
 *   · `TERMINATED` — kulrang, ishdan bo'shagan (yozuv tarix uchun qoladi)
 */
const STATE_COLOR: Record<PersonState, string> = {
  ACTIVE: 'green',
  SUSPENDED: 'gold',
  TERMINATED: 'default',
}

/**
 * Xodimlar reyestri — `xsib-hr` (`QARORLAR.md` §20.5, §20.7 5-qadam).
 *
 * <h2>⚠️ BU DAVOMAT XODIMLARI EMAS</h2>
 * §20.6 ikkalasini aniq ajratgan: `hr.person` — OBRO'LI MANBA,
 * `attendance.employee.full_name` — KESH (qurilma ekrani uchun nusxa).
 * Ism SHU YERDA tahrirlanadi va u davomatga sinxronlanadi, teskarisi
 * emas. Sahifa `attendance` API'siga umuman murojaat qilmaydi.
 *
 * <h2>⚠️ TUGUN FILTRI — `SUBTREE` EMAS</h2>
 * `PersonController#list` javadoc'i: `orgNodeId` AYNAN shu tugundagilar
 * degani. "Filial va uning ostidagilar" — bu ko'rinish DOIRASI va u
 * tokendan keladi. Ikkalasi birga ishlaydi, lekin ular BOSHQA narsa —
 * shuning uchun filtr yonida buni aytamiz, aks holda operator
 * "filialni tanladim, bo'limdagilar qayoqqa ketdi?" degan savolga
 * tushardi.
 *
 * <h2>⚠️ ISHDAN BO'SHAGANLAR SUKUT BO'YICHA KO'RINMAYDI (ADR-033)</h2>
 * 2026-08-21 da server `includeClosed` bayrog'ini oldi
 * (`PersonController#list`) va ilgari bu yerda yozilgan to'siq —
 * «yakka qiymatli `state`, ya'ni TERMINATED'siz hammasi so'rovi YO'Q» —
 * yopildi. Panel bayroqni HAR so'rovda ochiq yuboradi
 * ({@code personListParams}): serverdagi sukut qiymat `true`, ya'ni
 * yuborilmagani «hammasi» degani.
 *
 * <p>⚠️ «Yopiq» bu yerda FAQAT {@code TERMINATED}. {@code SUSPENDED}
 * QOLADI: u vaqtincha to'xtatilgan va qaytadi — yashirilsa qayta yoqish
 * yo'li ham qator bilan birga yo'qolardi.
 *
 * <p>⚠️ Ro'yxat SAHIFALANADI, shuning uchun mijozda kesish YO'Q va
 * bo'lmaydi ham: u faqat joriy sahifani toraytirardi, ya'ni «ishdan
 * bo'shagan xodim yo'q» degan ishonarli YOLG'ON berardi va sahifadan
 * sahifaga qatorlar soni sakrab turardi. Torayish EKRANDA — «5 / 8»
 * nishoni bilan aytiladi.
 *
 * <h2>⚠️ TUGUNLAR RO'YXATI ALOHIDA HUQUQ OSTIDA</h2>
 * U `platform` dan keladi (`org.node.read`). Faqat `hr.person.read` bor
 * operator ro'yxatni TO'LIQ ko'radi — tugun nomi javobning o'zida
 * keladi — lekin tugun bo'yicha filtrlay olmaydi. Sabab ekranda
 * yoziladi, filtr jimgina bo'sh qoldirilmaydi.
 */
export function HrPersonsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  /*
    ⚠️ Sukut qiymat `null` EMAS, `STATE_OPEN_ONLY`: `null` «filtr
    qo'yilmagan» degan ma'noni berardi, ro'yxat esa aslida ishdan
    bo'shaganlarsiz turardi (ADR-033 §1).
  */
  const [state, setState] = useState<PersonStateFilter>(STATE_OPEN_ONLY)
  const [orgNodeId, setOrgNodeId] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Person | null>(null)
  const [assigning, setAssigning] = useState<Person | null>(null)

  const nodeSource = useEmployeeNodes()

  const jobTitles = useQuery({
    queryKey: queryKeys.hr.jobTitles(true),
    queryFn: () => fetchJobTitles(true),
    staleTime: 5 * 60_000,
  })

  const filter = useMemo(() => ({ state, orgNodeId }), [state, orgNodeId])
  const fetchPage = useMemo(() => fetchPersonPage(filter), [filter])

  /*
    ⚠️ Yashiringan qatorlar soni SERVERDAN, mijozdagi hisobdan emas:
    ro'yxat sahifalangan va joriy sahifadagi qatorlarni sanash mahalliy
    va yolg'on son berardi (`useClosedHidden` javadoc'i).
  */
  const { state: tableState, patch: patchTable } = useTableUrlState()
  const countClosed = useMemo(() => countClosedPersons(filter), [filter])
  const hiddenCounts = useClosedHidden<Person>({
    queryKey: [...queryKeys.hr.persons, filter],
    state: tableState,
    fetchPage,
    countClosed,
    enabled: state === STATE_OPEN_ONLY,
  })

  /**
   * ⚠️ Holat almashganda sahifa BIRINCHIGA qaytariladi.
   *
   * Holat sahifaning O'Z holatida, sahifa raqami esa manzilda —
   * ya'ni ular avtomatik bog'lanmaydi. Usiz 3-sahifada turib
   * «Barcha holatlar» bosilsa, natija ikki sahifa bo'lsa ekran BO'SH
   * chiqardi va operator «yopilganlar baribir yo'q» degan xulosaga
   * kelardi — nishon aynan teskarisini aytib turgan holda.
   */
  const changeState = (next: PersonStateFilter) => {
    setState(next)
    patchTable({ page: 1 })
  }

  const columns = useMemo<ColumnsType<Person>>(
    () => [
      {
        key: 'fullName',
        title: t('hr.person.fullName'),
        width: 240,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Link onClick={() => void navigate(`/hr/persons/${row.id}`)}>
              {row.fullName}
            </Typography.Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('hr.person.personnelNoShort', { value: row.personnelNo })}
            </Typography.Text>
          </Space>
        ),
      },
      {
        key: 'jobTitle',
        title: t('hr.assignment.jobTitle'),
        width: 180,
        render: (_, row) => row.jobTitleName ? seededName('jobTitles', row.jobTitleCode, row.jobTitleName, t) : (row.jobTitleCode ?? '—'),
      },
      {
        key: 'orgNode',
        title: t('hr.assignment.orgNode'),
        width: 200,
        render: (_, row) => (
          <Space size={6} wrap>
            <Tooltip title={row.orgNodeCode ?? ''}>
              <span>{row.orgNodeName ?? row.orgNodeCode ?? '—'}</span>
            </Tooltip>
            {/*
              ⚠️ Ochiq bo'lmagan tayinlash ALOHIDA belgilanadi
              (`PersonView.assignmentOpen`): tugun ko'rinib turadi, lekin
              u ESKI. Buni ko'rsatmaslik operatorni joriy deb o'qishga
              majburlardi — va aynan shu holat ishdan bo'shaganlarda
              odatiy.
            */}
            {row.assignmentOpen ? null : (
              <Tooltip title={t('hr.person.assignmentClosedHint')}>
                <Tag color="gold">{t('hr.person.assignmentClosed')}</Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        key: 'state',
        title: t('hr.person.state'),
        width: 130,
        render: (_, row) => <Tag color={STATE_COLOR[row.state]}>{t(`personState.${row.state}`)}</Tag>,
      },
      {
        key: 'hiredAt',
        title: t('hr.person.hiredAt'),
        width: 130,
        // ⚠️ `LocalDate` — vaqt zonasidan MUSTAQIL. `formatDateTime`
        // qo'llash `Asia/Kabul` (UTC+4:30) da kunni siljitardi.
        render: (_, row) => row.hiredAt,
      },
      {
        key: 'phone',
        title: t('hr.person.phone'),
        width: 150,
        render: (_, row) => row.phone ?? '—',
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          /*
            ⚠️ TAYINLASH — ALOHIDA HUQUQ: u xodimning KIM TOMONIDAN
            KO'RINISHINI o'zgartiradi (`SUBTREE` doirasi), shuning uchun
            `hr.person.write` yetarli emas.
            ⚠️ Ishdan bo'shaganga yangi tayinlash berilmaydi — bazadagi
            `hr.person_state_guard` uni rad etadi, shuning uchun amal
            bunday qatorda umuman ko'rsatilmaydi (`hidden`).
          */
          <RowActions
            items={[
              {
                key: 'edit',
                label: t('common.edit'),
                onClick: () => {
                  setEditing(row)
                  setFormOpen(true)
                },
                permission: 'hr.person.write',
              },
              {
                key: 'assign',
                label: t('hr.assignment.assignAction'),
                onClick: () => setAssigning(row),
                permission: 'hr.employment.write',
                hidden: row.state === 'TERMINATED',
              },
            ]}
          />
        ),
      },
    ],
    [t, navigate],
  )

  return (
    <>
      <PageHeader
        title={t('nav.hrPersons')}
        description={t('pageIntro.hrPersons')}
        extra={
          <Can permission="hr.person.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('hr.person.createTitle')}
            </Button>
          </Can>
        }
      />

      {/*
        ⚠️ Tugunlar ro'yxati yo'qligi JIMGINA qoldirilmaydi. Bo'sh filtr
        "bo'lim ochilmagan" degan noto'g'ri xulosaga olib kelardi —
        `useSiteIndex` dagi `reliable` bayrog'i bilan bir xil sabab.
      */}
      {nodeSource.reason === 'no-permission' ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.person.nodeFilterNoPermission')}
        />
      ) : null}
      {nodeSource.reason === 'failed' ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.person.nodeFilterFailed')}
        />
      ) : null}

      <Space wrap style={{ marginBottom: 16 }}>
        {/*
          ⚠️ `allowClear` YO'Q va tanlov DOIM qiymat ko'rsatadi: bo'sh
          maydon «filtr qo'yilmagan» deb o'qilardi, ro'yxat esa aslida
          ishdan bo'shaganlarsiz turgan bo'lardi.
        */}
        <Tooltip title={t('dataTable.closedHiddenHint')}>
          <Select<PersonStateFilter>
            value={state}
            onChange={changeState}
            aria-label={t('hr.person.filterState')}
            style={{ width: 200 }}
            options={[
              { value: STATE_OPEN_ONLY, label: t('dataTable.stateOpenOnly') },
              ...PERSON_STATES.map((item) => ({
                value: item,
                label: t(`personState.${item}`),
              })),
              { value: STATE_ALL, label: t('dataTable.stateAll') },
            ]}
          />
        </Tooltip>
        <ClosedHiddenTag counts={hiddenCounts} onShowAll={() => changeState(STATE_ALL)} />
        {nodeSource.reason === 'ok' ? (
          <Tooltip title={t('hr.person.filterNodeHint')}>
            <div style={{ width: 300 }}>
              <OrgNodeTreeSelect
                nodes={nodeSource.nodes}
                types={nodeSource.types}
                value={orgNodeId}
                onChange={setOrgNodeId}
                loading={nodeSource.loading}
                allowClear
                placeholder={t('hr.person.filterNode')}
              />
            </div>
          </Tooltip>
        ) : null}
      </Space>

      <DataTable<Person>
        queryKey={[...queryKeys.hr.persons, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="hr-persons"
        searchable
        searchPlaceholder={t('hr.person.searchPlaceholder')}
        stickyFirstColumn
        /* Amallar ustuni 190 dan 56 ga qisqardi */
        scrollX={1066}
        emptyTitle={t('hr.person.empty')}
        /*
          ⚠️ Bo'sh ro'yxat «xodim yo'q» degani EMAS: ishdan bo'shaganlar
          sukut bo'yicha yashirin. Ikkinchi yo'l ko'rsatilmasa operator
          ularni butunlay o'chgan deb o'qirdi.
        */
        {...(hiddenCounts && hiddenCounts.hidden > 0
          ? {
              emptyDescription: t('dataTable.closedHiddenHint'),
              emptyAction: { label: t('dataTable.stateAll'), onClick: () => changeState(STATE_ALL) },
            }
          : { emptyDescription: t('hr.person.emptyHint') })}
      />

      <PersonFormModal
        open={formOpen}
        person={editing}
        nodes={nodeSource.nodes}
        types={nodeSource.types}
        jobTitles={jobTitles.data ?? []}
        nodesReliable={nodeSource.reliable}
        onClose={() => setFormOpen(false)}
      />

      {/* ⚠️ `key` — xodim almashganda modal QAYTA quriladi, aks holda
          oldingi xodimning joriy tuguni yangisiga meros bo'lardi. */}
      {assigning === null ? null : (
        <EmploymentAssignModal
          key={assigning.id}
          open
          person={assigning}
          nodes={nodeSource.nodes}
          types={nodeSource.types}
          jobTitles={jobTitles.data ?? []}
          nodesReliable={nodeSource.reliable}
          onClose={() => setAssigning(null)}
        />
      )}
    </>
  )
}
